import type { CreateTodo, Todo } from "@ronin/contracts";
import { Button, cn, DateInput, Dialog, Input, Kbd, Label, Textarea, TimeInput } from "@ronin/ui";
import { Trash2, Zap } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useId, useState } from "react";
import { localDatePlus } from "../../lib/due.js";
import { useTimeZone } from "../settings/use-settings.js";

export interface TodoFormValues {
  title: string;
  description: string;
  important: boolean;
  dueDate: string;
  dueTime: string;
}

const empty: TodoFormValues = {
  title: "",
  description: "",
  important: false,
  dueDate: "",
  dueTime: "",
};

export const valuesFromTodo = (todo: Todo): TodoFormValues => ({
  title: todo.title,
  description: todo.description ?? "",
  important: todo.important,
  dueDate: todo.dueDate ?? "",
  dueTime: todo.dueTime ?? "",
});

/** Form values to API input; empty fields become null. */
export const toInput = (v: TodoFormValues) =>
  ({
    title: v.title.trim(),
    description: v.description.trim() || null,
    important: v.important,
    dueDate: v.dueDate || null,
    dueTime: v.dueDate ? v.dueTime || null : null,
  }) satisfies CreateTodo;

const quickPicks = [
  { label: "Today", days: 0 },
  { label: "Tomorrow", days: 1 },
  { label: "Next week", days: 7 },
];

interface TodoDialogProps {
  open: boolean;
  /** The todo being edited; undefined = create. */
  todo?: Todo;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: TodoFormValues) => Promise<unknown>;
  onDelete?: (todo: Todo) => void;
}

export function TodoDialog({ open, todo, onOpenChange, onSubmit, onDelete }: TodoDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={todo ? "Edit todo" : "New todo"}
      description={todo ? undefined : "Only the title is required."}
    >
      {/* Remount per todo so the form starts from its values. */}
      <TodoForm
        key={todo?.id ?? "new"}
        todo={todo}
        onSubmit={onSubmit}
        onCancel={() => onOpenChange(false)}
        onDelete={onDelete}
      />
    </Dialog>
  );
}

interface TodoFormProps {
  todo?: Todo;
  onSubmit: (values: TodoFormValues) => Promise<unknown>;
  onCancel: () => void;
  onDelete?: (todo: Todo) => void;
}

function TodoForm({ todo, onSubmit, onCancel, onDelete }: TodoFormProps) {
  const id = useId();
  const timeZone = useTimeZone();
  const [values, setValues] = useState(todo ? valuesFromTodo(todo) : empty);
  const [pending, setPending] = useState(false);
  const set = <K extends keyof TodoFormValues>(key: K, value: TodoFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const canSave = values.title.trim() !== "" && !pending;

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!canSave) return;
    setPending(true);
    try {
      await onSubmit(values);
    } catch {
      // The error toast comes from the mutation; keep the dialog open with the input.
    } finally {
      setPending(false);
    }
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} onKeyDown={onKeyDown}>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-title`}>Title</Label>
        <Input
          id={`${id}-title`}
          value={values.title}
          maxLength={200}
          placeholder="e.g. Pay rent"
          data-autofocus
          onChange={(e) => set("title", e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-description`}>
          Description <span className="font-normal text-faint-foreground">(optional)</span>
        </Label>
        <Textarea
          id={`${id}-description`}
          value={values.description}
          maxLength={5000}
          placeholder="Add details…"
          onChange={(e) => set("description", e.target.value)}
        />
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">
          Due <span className="font-normal text-faint-foreground">(optional)</span>
        </legend>
        <div className="flex flex-wrap items-stretch gap-2">
          <Label htmlFor={`${id}-date`} className="sr-only">
            Due date
          </Label>
          <DateInput
            id={`${id}-date`}
            className="min-w-36 flex-1"
            value={values.dueDate}
            onValueChange={(value) => set("dueDate", value)}
          />
          <Label htmlFor={`${id}-time`} className="sr-only">
            Due time
          </Label>
          <TimeInput
            id={`${id}-time`}
            className="w-28"
            value={values.dueTime}
            disabled={!values.dueDate}
            title={values.dueDate ? undefined : "Pick a date first"}
            onValueChange={(value) => set("dueTime", value)}
          />
          <button
            type="button"
            aria-pressed={values.important}
            title={values.important ? "Unmark as important" : "Mark as important"}
            onClick={() => set("important", !values.important)}
            className={cn(
              "flex h-9 shrink-0 items-center gap-1.5 border px-3 text-sm transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              values.important
                ? "border-important/30 bg-important/10 text-important"
                : "border-input text-muted-foreground hover:bg-muted",
            )}
          >
            <Zap
              className="size-4"
              fill={values.important ? "currentColor" : "none"}
              fillOpacity={0.25}
            />
            Important
          </button>
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {quickPicks.map(({ label, days }) => {
            const date = localDatePlus(days, new Date(), timeZone);
            return (
              <Button
                key={label}
                type="button"
                variant="secondary"
                size="sm"
                aria-pressed={values.dueDate === date}
                className="h-7 aria-pressed:border-ring aria-pressed:bg-accent aria-pressed:text-accent-foreground"
                onClick={() => set("dueDate", date)}
              >
                {label}
              </Button>
            );
          })}
          {values.dueDate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() => setValues((v) => ({ ...v, dueDate: "", dueTime: "" }))}
            >
              Clear
            </Button>
          )}
        </div>
      </fieldset>

      <div className="mt-2 flex items-center gap-2">
        {todo && onDelete && (
          <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(todo)}>
            <Trash2 />
            Delete
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSave}>
            {todo ? "Save" : "Create todo"}
            <Kbd className="hidden sm:inline-flex">⌘↵</Kbd>
          </Button>
        </div>
      </div>
    </form>
  );
}
